import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import StbButton from '../security-toolbox-ui/StbButton';
import StbModal from '../security-toolbox-ui/StbModal';
import { useLabAssistAction, useLabAssistCase } from '../../hooks/lab-hooks';
import {
  LabAssistAccessMethod,
  LabAssistAction,
  LabAssistCase,
  LabAssistEventKind,
  LabAssistState,
  LAB_ASSIST_ACCESS_DURATIONS,
  LAB_ASSIST_ACCESS_METHODS,
  LAB_ASSIST_EVENT_KINDS,
  LAB_ASSIST_RESOLVE_STATES,
  labAssistSentiment,
} from '../../support/labService';
import {
  LabCard,
  LabDate,
  LabJsonView,
  LabOptionSelect,
  LabPanelState,
  LabStatusTag,
  labErrorMessage,
} from './LabShared';

type Dialog = 'note' | 'grant' | 'revoke' | 'resolve' | null;

export type LabAssistCaseDetailProps = {
  reference?: string;
  onClose?: () => void;
};

const stateSentiment = (state: LabAssistState) => {
  if (state === 'resolved') return 'success' as const;
  if (state === 'closed_unresolved') return 'danger' as const;
  if (state === 'cancelled') return 'warning' as const;
  return 'info' as const;
};

/**
 * One assist case: what happened, in order, and the five things you can do about it.
 *
 * The timeline mixes events people wrote with events the service wrote. Only four kinds are
 * offered in the note dialog - `state_change`, `access_granted` and `access_revoked` are the
 * service's own record of something it actually did, so a person cannot post "access revoked"
 * while their access is still live.
 *
 * Access is shown by `access_is_current`, not by "was it ever granted": a case with an expired
 * window reads as no access, which is what it is.
 */
const LabAssistCaseDetail: React.FC<LabAssistCaseDetailProps> = ({
  reference: referenceProp,
  onClose,
}) => {
  const navigate = useNavigate();
  const params = useParams();
  const reference = referenceProp || params.reference;
  const [dialog, setDialog] = useState<Dialog>(null);
  const [noteSummary, setNoteSummary] = useState('');
  const [noteKind, setNoteKind] = useState<LabAssistEventKind>('note');
  const [accessMethod, setAccessMethod] = useState<LabAssistAccessMethod>('radkit');
  const [accessDuration, setAccessDuration] = useState('7200');
  const [revokeReason, setRevokeReason] = useState('');
  const [resolveState, setResolveState] = useState<LabAssistState>('resolved');
  const [resolution, setResolution] = useState('');
  const [rootCause, setRootCause] = useState('');
  const [fixApplied, setFixApplied] = useState('');
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const { data: assistCase, isLoading, error } = useLabAssistCase(reference);
  const act = useLabAssistAction();

  useEffect(() => {
    setNoteSummary('');
    setNoteKind('note');
    setRevokeReason('');
    setResolution('');
    setRootCause('');
    setFixApplied('');
    setDialogError(null);
  }, [dialog]);

  const run = async (action: LabAssistAction) => {
    if (!reference) return false;

    setDialogError(null);
    setActionError(null);

    try {
      await act.mutateAsync({ reference, action });
      return true;
    } catch (exc) {
      const message = labErrorMessage(exc, 'The case would not take that.');

      if (dialog) {
        setDialogError(message);
      } else {
        setActionError(message);
      }

      return false;
    }
  };

  if (isLoading || error || !assistCase) {
    return (
      <LabCard title="Assist case">
        <LabPanelState
          loading={isLoading}
          error={error}
          empty={!assistCase}
          // Same reasoning as runs: a case you have no stake in is a 404, not a 403.
          emptyMessage="No case here. Either it does not exist, or somebody else is already handling it."
          loadingLabel="Loading case..."
        >
          <span />
        </LabPanelState>
      </LabCard>
    );
  }

  const loaded = assistCase as LabAssistCase;

  return (
    <>
      <LabCard
        title={loaded.subject}
        subtitle={
          <span>
            {loaded.reference}
            {` • ${loaded.session_key}`}
            {loaded.device_key ? ` • ${loaded.device_key}` : ''}
          </span>
        }
        actions={
          <>
            <StbButton
              role="default"
              size="small"
              onClick={async () => (onClose ? onClose() : navigate('/lab/operations'))}
            >
              Back
            </StbButton>

            {loaded.is_open && (
              <>
                {!loaded.assigned_to_email && (
                  <StbButton
                    role="request"
                    size="small"
                    loading={act.isPending}
                    onClick={async () => run({ action: 'assign' })}
                  >
                    Take it
                  </StbButton>
                )}

                <StbButton role="add" size="small" onClick={async () => setDialog('note')}>
                  Add a note
                </StbButton>

                {loaded.access_is_current ? (
                  <StbButton role="delete" size="small" onClick={async () => setDialog('revoke')}>
                    Revoke access
                  </StbButton>
                ) : (
                  <StbButton role="update" size="small" onClick={async () => setDialog('grant')}>
                    Grant access
                  </StbButton>
                )}

                <StbButton role="confirm" size="small" onClick={async () => setDialog('resolve')}>
                  Resolve
                </StbButton>
              </>
            )}
          </>
        }
      >
        <div className="flex flex-wrap items-center gap-3">
          <LabStatusTag label={loaded.state} sentiment={stateSentiment(loaded.state)} />
          <LabStatusTag
            label={loaded.severity}
            sentiment={labAssistSentiment(loaded.severity)}
          />
          <span className="text-xs text-gray-500">{`raised by ${loaded.opened_by_email}`}</span>
          <span className="text-xs text-gray-500">
            {loaded.assigned_to_email ? `handled by ${loaded.assigned_to_email}` : 'unassigned'}
          </span>
          <span className="text-xs text-gray-500">{`via ${loaded.origin}`}</span>
        </div>

        {loaded.detail && (
          <p className="m-0 mt-3 whitespace-pre-wrap text-sm text-gray-700">{loaded.detail}</p>
        )}

        <div className="mt-3 grid grid-cols-1 gap-3 text-sm md:grid-cols-3">
          <div>
            <div className="text-xs uppercase text-gray-500">Opened</div>
            <LabDate value={loaded.opened_at} />
          </div>

          <div>
            <div className="text-xs uppercase text-gray-500">Access</div>
            {loaded.access_is_current ? (
              <div>
                {`${loaded.access_method} until `}
                <LabDate value={loaded.access_expires_at} />
                {loaded.access_granted_by_email && (
                  <div className="text-xs text-gray-500">
                    {`granted by ${loaded.access_granted_by_email}`}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-gray-600">
                {loaded.access_granted_at ? 'Expired or revoked' : 'None granted'}
              </div>
            )}
          </div>

          <div>
            <div className="text-xs uppercase text-gray-500">Resolved</div>
            <LabDate value={loaded.resolved_at} fallback="Not yet" />
          </div>
        </div>

        {(loaded.root_cause || loaded.fix_applied || loaded.resolution) && (
          <div className="mt-3 rounded border border-gray-200 bg-gray-50 p-3 text-sm">
            {loaded.root_cause && (
              <div>
                <span className="text-xs uppercase text-gray-500">Root cause </span>
                {loaded.root_cause}
              </div>
            )}
            {loaded.fix_applied && (
              <div>
                <span className="text-xs uppercase text-gray-500">Fix </span>
                {loaded.fix_applied}
              </div>
            )}
            {loaded.resolution && (
              <div className="whitespace-pre-wrap">{loaded.resolution}</div>
            )}
          </div>
        )}

        {loaded.run && (
          <div className="mt-3">
            <StbButton
              role="request"
              size="xsmall"
              onClick={async () => navigate(`/lab/runs/${loaded.run}`)}
            >
              Open the run
            </StbButton>
          </div>
        )}

        {actionError && <p className="m-0 mt-2 text-sm text-amber-800">{actionError}</p>}
      </LabCard>

      <LabCard title="Timeline" subtitle="Oldest first. Service entries are marked.">
        {(loaded.events || []).length ? (
          <ol className="m-0 list-none space-y-3 p-0">
            {[...(loaded.events || [])]
              .sort((left, right) => left.occurred_at.localeCompare(right.occurred_at))
              .map((event) => {
                const authorable = LAB_ASSIST_EVENT_KINDS.some(
                  (option) => option.value === event.kind,
                );

                return (
                  <li key={event.id} className="border-b border-gray-100 pb-2 last:border-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <LabStatusTag
                        label={event.kind.replace('_', ' ')}
                        sentiment={authorable ? 'info' : 'interact'}
                      />
                      <span className="text-xs text-gray-500">
                        {event.actor_email || 'the service'}
                      </span>
                      <span className="text-xs text-gray-400">
                        <LabDate value={event.occurred_at} />
                      </span>
                    </div>

                    <div className="mt-1 whitespace-pre-wrap text-sm">{event.summary}</div>

                    {event.detail && (
                      <details className="mt-1">
                        <summary className="cursor-pointer text-xs text-gray-500">Detail</summary>
                        <LabJsonView value={event.detail} />
                      </details>
                    )}
                  </li>
                );
              })}
          </ol>
        ) : (
          <p className="m-0 text-sm text-gray-600">Nothing recorded yet.</p>
        )}
      </LabCard>

      <StbModal
        open={dialog === 'note'}
        role="update"
        title="Add to the case"
        submitLabel="Add"
        submitDisabled={act.isPending}
        suppressCloseOnSubmit
        onSubmit={async () => {
          if (!noteSummary.trim()) {
            setDialogError('Say what happened.');
            return;
          }

          if (await run({ action: 'note', summary: noteSummary.trim(), kind: noteKind })) {
            setDialog(null);
          }
        }}
        onClose={async () => setDialog(null)}
        apiResponseSection={
          dialogError ? <p className="m-0 text-sm text-amber-800">{dialogError}</p> : undefined
        }
      >
        <div className="flex flex-col gap-4">
          <LabOptionSelect
            label="Kind"
            value={noteKind}
            options={LAB_ASSIST_EVENT_KINDS}
            onChange={(next) => setNoteKind(next)}
            helpText="Diagnostic for what you found, remediation for what you changed."
          />

          <TextField
            label="Summary"
            value={noteSummary}
            onChange={(evt) => setNoteSummary(evt.target.value)}
            multiline
            minRows={3}
            fullWidth
          />
        </div>
      </StbModal>

      <StbModal
        open={dialog === 'grant'}
        role="update"
        title="Grant access to this pod"
        submitLabel="Grant"
        submitDisabled={act.isPending}
        suppressCloseOnSubmit
        onSubmit={async () => {
          if (
            await run({
              action: 'grant_access',
              method: accessMethod,
              duration_seconds: Number(accessDuration),
            })
          ) {
            setDialog(null);
          }
        }}
        onClose={async () => setDialog(null)}
        apiResponseSection={
          dialogError ? <p className="m-0 text-sm text-amber-800">{dialogError}</p> : undefined
        }
      >
        <div className="flex flex-col gap-4">
          <p className="m-0 text-sm text-gray-700">
            {`This records that you may reach into ${loaded.session_key}, and until when. The window always ends - resolving the case ends it early.`}
          </p>

          <LabOptionSelect
            label="Method"
            value={accessMethod}
            options={LAB_ASSIST_ACCESS_METHODS}
            onChange={(next) => setAccessMethod(next)}
            helpText="How you are getting in. Recorded on the case."
          />

          <LabOptionSelect
            label="For how long"
            value={accessDuration}
            options={LAB_ASSIST_ACCESS_DURATIONS}
            onChange={(next) => setAccessDuration(next)}
          />
        </div>
      </StbModal>

      <StbModal
        open={dialog === 'revoke'}
        role="delete"
        title="Revoke access"
        submitLabel="Revoke"
        submitButtonRole="delete"
        submitDisabled={act.isPending}
        suppressCloseOnSubmit
        onSubmit={async () => {
          if (await run({ action: 'revoke_access', reason: revokeReason.trim() })) {
            setDialog(null);
          }
        }}
        onClose={async () => setDialog(null)}
        apiResponseSection={
          dialogError ? <p className="m-0 text-sm text-amber-800">{dialogError}</p> : undefined
        }
      >
        <div className="flex flex-col gap-3">
          <p className="m-0 text-sm text-gray-700">
            Ends the access window now. The case stays open.
          </p>

          <TextField
            label="Reason (optional)"
            value={revokeReason}
            onChange={(evt) => setRevokeReason(evt.target.value)}
            fullWidth
          />
        </div>
      </StbModal>

      <StbModal
        open={dialog === 'resolve'}
        role="update"
        title="Close the case"
        size="large"
        submitLabel="Close it"
        submitDisabled={act.isPending}
        suppressCloseOnSubmit
        onSubmit={async () => {
          if (
            await run({
              action: 'resolve',
              state: resolveState,
              resolution: resolution.trim(),
              root_cause: rootCause.trim(),
              fix_applied: fixApplied.trim(),
            })
          ) {
            setDialog(null);
          }
        }}
        onClose={async () => setDialog(null)}
        apiResponseSection={
          dialogError ? <p className="m-0 text-sm text-amber-800">{dialogError}</p> : undefined
        }
      >
        <div className="flex flex-col gap-4">
          <LabOptionSelect
            label="Outcome"
            value={resolveState}
            options={LAB_ASSIST_RESOLVE_STATES}
            onChange={(next) => setResolveState(next)}
          />

          <TextField
            label="Root cause"
            value={rootCause}
            onChange={(evt) => setRootCause(evt.target.value)}
            helperText="Keep it short and reusable. The same label on eleven pods is how a broken lab gets noticed; prose describes one pod."
            fullWidth
          />

          <TextField
            label="Fix applied"
            value={fixApplied}
            onChange={(evt) => setFixApplied(evt.target.value)}
            fullWidth
          />

          <TextField
            label="Resolution"
            value={resolution}
            onChange={(evt) => setResolution(evt.target.value)}
            multiline
            minRows={3}
            fullWidth
          />

          <p className="m-0 text-xs text-gray-500">
            Closing the case revokes any access that is still granted.
          </p>
        </div>
      </StbModal>
    </>
  );
};

export default LabAssistCaseDetail;
